import type { LocalDb } from './db.ts';
import type { AccountEntryRow, CustomerBalanceRow } from './types.ts';

/**
 * Saldo de cada cliente en este dispositivo, igual que el stock:
 *   saldo del servidor (customer_balances) + movimientos locales que ese saldo todavía no incluye
 * (un movimiento está incluido si el servidor ya lo aplicó y el saldo bajado es de esa
 * transacción o posterior). Los movimientos rechazados no cuentan.
 */
export function computeBalances(
  balances: CustomerBalanceRow[],
  entries: AccountEntryRow[],
  rejectedIds: ReadonlySet<string> = new Set(),
): Map<string, number> {
  const result = new Map<string, number>();
  const xidByCustomer = new Map<string, bigint>();
  for (const b of balances) {
    result.set(b.customer_id, (result.get(b.customer_id) ?? 0) + b.balance);
    xidByCustomer.set(b.customer_id, BigInt(b.server_xid ?? '0'));
  }
  for (const e of entries) {
    if (rejectedIds.has(e.id)) continue;
    const xid = xidByCustomer.get(e.customer_id);
    if (e.server_xid !== undefined && xid !== undefined && xid >= BigInt(e.server_xid)) continue;
    result.set(e.customer_id, (result.get(e.customer_id) ?? 0) + e.amount);
  }
  return result;
}

/** Movimientos de cuenta corriente que el servidor rechazó (los propios y los de ventas rechazadas). */
export async function rejectedAccountEntryIds(db: LocalDb): Promise<Set<string>> {
  const ops = await db.outbox.where('[table+status]').anyOf([['account_entries', 'rejected'], ['sales', 'rejected']]).toArray();
  return new Set(
    ops.flatMap((o) =>
      o.table === 'sales' ? ((o.payload.payments as Array<{ id: string; method: string }> | undefined) ?? []).filter((p) => p.method === 'account').map((p) => p.id) : [o.row_id],
    ),
  );
}

/**
 * Limpieza: movimientos de cuenta corriente ya incluidos en el saldo del servidor (de más de
 * `days` días) y movimientos de caja de cajas cerradas y enviadas hace más de 60 días.
 */
export async function pruneCashAndAccounts(db: LocalDb, days = 3, now = Date.now()): Promise<number> {
  const cutoff = new Date(now - days * 86_400_000).toISOString();
  const oldCutoff = new Date(now - 60 * 86_400_000).toISOString();
  return db.transaction('rw', [db.account_entries, db.customer_balances, db.cash_movements, db.cash_sessions], async () => {
    const old = await db.account_entries.where('created_at').below(cutoff).toArray();
    const balances = await db.customer_balances.where('customer_id').anyOf([...new Set(old.map((e) => e.customer_id))]).toArray();
    const xid = new Map(balances.map((b) => [b.customer_id, BigInt(b.server_xid ?? '0')]));
    const settled = old.filter((e) => e.server_xid !== undefined && (xid.get(e.customer_id) ?? -1n) >= BigInt(e.server_xid)).map((e) => e.id);
    await db.account_entries.bulkDelete(settled);

    const oldSessions = await db.cash_sessions.filter((s) => s.closed_at !== null && s.closed_at < oldCutoff && s.server_xid !== undefined).primaryKeys();
    const moves = await db.cash_movements.where('session_id').anyOf(oldSessions).filter((m) => m.server_xid !== undefined).primaryKeys();
    await db.cash_movements.bulkDelete(moves);
    return settled.length + moves.length;
  });
}
